import { useState } from 'react';
import type { AppDefinition } from '@/types/app';
import { useSession } from '../../state/useSession';
import { useProjects } from '../../state/projects';
import { publishLatest, storedDefinition } from '../../state/livePage';
import { changeSiteAddress, setAiBilling } from '../../api/owner';
import type { SiteCheck } from '../../state/siteStatus';
import { Button, TextField } from '../../ui';

/**
 * The owner tools the phone has and the web did not (F24, deep-dive 2026-09-30): the app's name,
 * the site's address, and who pays for its AI. `changeSiteAddress` had no caller on the web.
 *
 * Each writes onto the record as STORED, read just before, never onto the copy this screen was
 * drawn with — a chat edit may have landed since. And nothing here runs while a chat edit is on
 * its way (`editing`), since a rename republishes and an edit's own publish would race it.
 */
export default function SiteSettings({
  projectId,
  name,
  definition,
  site,
  editing,
  onSiteChange,
}: {
  projectId: string;
  name: string;
  definition: AppDefinition;
  site: SiteCheck;
  editing: boolean;
  onSiteChange: (next: SiteCheck) => void;
}) {
  return (
    <section className="site-settings" aria-label="Site settings">
      <NameRow projectId={projectId} name={name} definition={definition} site={site} editing={editing} />
      <AddressRow projectId={projectId} definition={definition} site={site} editing={editing} onSiteChange={onSiteChange} />
      <AiBillingRow projectId={projectId} definition={definition} site={site} />
    </section>
  );
}

/** Whether the site has a page the server knows about — what a republish or a setting reaches. */
function hasPage(definition: AppDefinition, site: SiteCheck): boolean {
  return Boolean(definition.hostedAppId) && site.kind !== 'none';
}

/**
 * The app's name. It is the page's title, so a site with a page is republished — as a CONTENT
 * update, which keeps an offline page offline and a password page behind its password. The
 * address stays where it is: moving that is the row below, on purpose.
 */
function NameRow({
  projectId,
  name,
  definition,
  site,
  editing,
}: {
  projectId: string;
  name: string;
  definition: AppDefinition;
  site: SiteCheck;
  editing: boolean;
}) {
  const { user } = useSession();
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function save(): Promise<void> {
    const next = (draft ?? '').trim();
    if (!next || saving) return;
    setSaving(true);
    setError(null);
    setDone(null);
    const ownerId = user?.id ?? '';
    try {
      await useProjects.getState().updateProject(projectId, { name: next }, ownerId);
      setDraft(null);
      if (hasPage(definition, site)) {
        try {
          await publishLatest(projectId, ownerId);
          setDone(`Renamed to ${next}. Your page’s title is updated.`);
        } catch (e) {
          setDone(`Renamed to ${next}.`);
          setError(`Your page still shows the old name: ${e instanceof Error && e.message ? e.message : 'it could not be updated.'} Publish changes to try again.`);
        }
      } else {
        setDone(`Renamed to ${next}.`);
      }
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'That name could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-row" role="group" aria-label="Name">
      <p className="type-caption field-label">Name</p>
      {draft === null ? (
        <div className="settings-row-line">
          <span className="type-body">{name}</span>
          <Button title="Rename" variant="ghost" disabled={editing} onPress={() => { setDraft(name); setDone(null); setError(null); }} />
        </div>
      ) : (
        <>
          <TextField label="New name" value={draft} onChangeText={setDraft} autoFocus />
          <div className="build-actions">
            <Button title="Save name" loading={saving} disabled={!draft.trim() || editing} onPress={() => void save()} />
            <Button title="Cancel" variant="ghost" onPress={() => setDraft(null)} />
          </div>
        </>
      )}
      {done && <p className="type-caption" role="status">{done}</p>}
      {error && <p role="alert" data-error-code="site_rename" className="type-caption field-error-text">{error}</p>}
    </div>
  );
}

/**
 * Where the site lives. Moved on the server, which keeps the old address working — somebody
 * has that link. The phone's SiteAddressControl, in its words.
 */
function AddressRow({
  projectId,
  definition,
  site,
  editing,
  onSiteChange,
}: {
  projectId: string;
  definition: AppDefinition;
  site: SiteCheck;
  editing: boolean;
  onSiteChange: (next: SiteCheck) => void;
}) {
  const { user } = useSession();
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const url = (site.kind === 'known' && site.siteUrl) || definition.webUrl;
  // Nothing to move until the app has an address.
  if (!url || !definition.hostedAppId) return null;
  const current = url.replace(/^.*\/site\//, '').replace(/\/+$/, '');

  async function save(): Promise<void> {
    const desired = (draft ?? '').trim();
    if (!desired || saving) return;
    setSaving(true);
    setError(null);
    setDone(null);
    try {
      const result = await changeSiteAddress(definition.hostedAppId, desired);
      if (result.siteUrl) {
        const ownerId = user?.id ?? '';
        const now = await storedDefinition(projectId, ownerId);
        await useProjects.getState().updateProject(projectId, { definition: { ...now, webUrl: result.siteUrl } }, ownerId);
        if (site.kind === 'known') onSiteChange({ ...site, siteUrl: result.siteUrl });
      }
      setDraft(null);
      setDone(
        result.keptWorking.length > 0
          ? `Your site is now at ${result.slug}. The old link still opens it.`
          : `Your site is now at ${result.slug}.`,
      );
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'The address could not be changed. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-row" role="group" aria-label="Address">
      <p className="type-caption field-label">Address</p>
      {draft === null ? (
        <div className="settings-row-line">
          <span className="type-body settings-address">{url}</span>
          <Button title="Change address" variant="ghost" disabled={editing} onPress={() => { setDraft(current); setDone(null); setError(null); }} />
        </div>
      ) : (
        <>
          <TextField
            label="New address"
            value={draft}
            onChangeText={setDraft}
            placeholder="fade-room"
            hint="Letters, numbers and dashes. The old address keeps working."
            autoFocus
          />
          <div className="build-actions">
            <Button title="Save address" loading={saving} disabled={!draft.trim() || editing} onPress={() => void save()} />
            <Button title="Cancel" variant="ghost" onPress={() => { setDraft(null); setError(null); }} />
          </div>
        </>
      )}
      {done && <p className="type-caption" role="status">{done}</p>}
      {error && <p role="alert" data-error-code="site_address" className="type-caption field-error-text">{error}</p>}
    </div>
  );
}

/**
 * Whose credits the site's AI spends — the phone's AiBillingControl. Offered for a site that
 * uses AI (`S.ai.`, the phone's test) and whose page the server has confirmed exists: the
 * setting goes through the publish route, and for a row that never had a page that route would
 * put the page up rather than leave it alone.
 *
 * They pay makes signing in mandatory, whatever else the site says, because there is nobody to
 * bill for an anonymous call — said before the choice, not discovered later.
 */
function AiBillingRow({
  projectId,
  definition,
  site,
}: {
  projectId: string;
  definition: AppDefinition;
  site: SiteCheck;
}) {
  const { user } = useSession();
  const [current, setCurrent] = useState<'creator' | 'requester'>(definition.aiBilling ?? 'creator');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!(definition.source ?? '').includes('S.ai.') || !definition.hostedAppId || site.kind !== 'known') return null;

  const OPTIONS: { id: 'creator' | 'requester'; label: string; hint: string }[] = [
    { id: 'creator', label: 'You pay', hint: 'AI on your site comes out of your credits.' },
    { id: 'requester', label: 'They pay', hint: 'Each person spends their own credits, and has to sign in first.' },
  ];

  async function choose(id: 'creator' | 'requester'): Promise<void> {
    if (id === current || saving) return;
    setSaving(true);
    setError(null);
    try {
      await setAiBilling(definition.hostedAppId, id);
      setCurrent(id);
      const ownerId = user?.id ?? '';
      const now = await storedDefinition(projectId, ownerId);
      await useProjects.getState().updateProject(projectId, { definition: { ...now, aiBilling: id } }, ownerId);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'That could not be changed. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-row">
      <p className="type-caption field-label" id="ai-billing-label">Who pays for AI</p>
      <div className="chat-mode" role="radiogroup" aria-labelledby="ai-billing-label">
        {OPTIONS.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={current === o.id}
            aria-describedby={`ai-billing-${o.id}`}
            className={current === o.id ? 'is-selected' : ''}
            disabled={saving}
            onClick={() => void choose(o.id)}
          >
            {o.label}
          </button>
        ))}
      </div>
      {OPTIONS.map((o) => (
        <p key={o.id} id={`ai-billing-${o.id}`} className="type-caption">
          <strong>{o.label}:</strong> {o.hint}
        </p>
      ))}
      {error && <p role="alert" data-error-code="site_ai_billing" className="type-caption field-error-text">{error}</p>}
    </div>
  );
}
